<template>
  <div class="order-form">
    <button class="btn toggle-btn is-dark" @click="activateOrderForm">
      Cart ({{ this.totalQty }})
    </button>

    <form v-if="hasItems" @submit.prevent="submit">
      <h2 class="centered">Your Order</h2>
      <table class="order-details">
        <thead>
          <td class="line-item-image"></td>
          <td>Product</td>
          <td>Quantity</td>
          <td>Price</td>
          <td class="remove-line-item"></td>
        </thead>

        <tbody>
          <tr v-for="item in lineItems" :key="item.title">
            <td class="line-item-image">
              <LazyImage :url="item.image.url" />
            </td>
            <td>{{ item.title }}</td>
            <td class="product-qty">
              <ProductQuantity
                class="editable-qty"
                :currentQty="item.qty"
                @updateQuantity="updateQuantity($event, item)"
              />
              <span>{{ item.qty }}</span>
            </td>
            <td>{{ item.currency }}{{ lineItemPrice(item) }}</td>
            <td class="remove-line-item">
              <button @click="removeFromCart(item)">
                <Sprite name="bin" />
              </button>
            </td>
          </tr>
          <tr class="line-items-total">
            <td class="line-item-image"></td>
            <td></td>
            <td></td>
            <td><strong>Subtotal</strong></td>
            <td>
              <strong>{{ currency }}{{ parseFloat(totalPrice).toFixed(2) }}</strong>
            </td>
          </tr>
        </tbody>
      </table>

      <div>
        <h2 class="centered">Your Details</h2>

        <field
          label="name"
          @input="$v.resource.sender_name.$touch()"
          v-model="resource.sender_name"
          :error="sender_nameError"
        >
        </field>

        <field
          label="email"
          @input="$v.resource.sender_email.$touch()"
          v-model="resource.sender_email"
          :error="sender_emailError"
        >
        </field>

        <div class="input">
          <label for="notes">Additional Notes</label>
          <textarea v-model="additionalNotes" rows="5" id="notes"></textarea>
        </div>

        <button class="btn send-message">Submit Order</button>
      </div>
    </form>

    <div v-else>
      <p>Your cart is currently empty.</p>
    </div>
  </div>
</template>

<script>
import form from '@/mixins/form'

import {errorMessages} from '@/lib/error_messages'
import {email, required} from 'vuelidate/lib/validators'

import LazyImage from '@/components/LazyImage'
import ProductQuantity from './ProductQuantity'
import Sprite from '@/components/Sprite'

export default {
  name: 'order-form',

  data() {
    return {
      additionalNotes: ''
    }
  },

  mixins: [form],

  props: {
    lineItems: Array,
    totalQty: Number,
    totalPrice: Number,
    currency: String
  },

  components: {
    LazyImage,
    ProductQuantity,
    Sprite
  },

  computed: {
    ...errorMessages(['sender_name', 'sender_email', 'body']),

    hasItems() {
      return this.totalQty > 0
    },

    itemPrice() {
      return (item) => {
        let price = parseFloat(item.price)
        return price.toFixed(2)
      }
    },

    lineItemPrice() {
      return (item) => {
        let price = parseFloat(item.price)
        return (price * item.qty).toFixed(2)
      }
    },

    formattedBody() {
      let string =
        'Congratulations! A customer has requested the following items:\n\n'

      this.lineItems.forEach((item) => {
        string += `Product: ${item.title}\n`
        string += `Price: ${item.currency}${item.qty * item.price} (${
          item.currency
        }${item.price} each)\n`
        string += `Quantity: ${item.qty}\n\n`
      })

      string += `Total Quantity: ${this.totalQty}\n`
      string += `Total Amount: ${this.currency}${this.totalPrice}\n\n`

      string += 'Additional Notes:\n'
      string += this.additionalNotes

      return string
    }
  },

  methods: {
    activateOrderForm() {
      this.$emit('activateOrderForm', 'order-form')
    },

    updateQuantity(qty, item) {
      this.$emit('updateQuantity', qty, item)
    },

    removeFromCart(product) {
      this.$emit('removeFromCart', product)
    }
  },

  validations: {
    resource: {
      sender_name: {required},
      sender_email: {required, email}
    }
  },

  mounted() {
    this.resource.body = this.formattedBody
  },

  watch: {
    lineItems() {
      this.resource.body = this.formattedBody
    },

    additionalNotes() {
      this.resource.body = this.formattedBody
    }
  }
}
</script>

<style lang="sass">
@import '../../../stylesheets/base/variables'
@import '../../../stylesheets/helpers/mixins'
@import '../../../stylesheets/base/theme'

.order-form
  max-height: 34px
  transition: all .5s .5s ease-in-out
  position: relative
  overflow: hidden

  .toggle-btn
    .sprite
      width: 15px
      height: 15px
      position: absolute
      top: 18px
      right: $gap-medium

  .remove-line-item
    max-width: 50px
    text-align: center

    button
      background: none

    .sprite
      width: 20px
      height: 20px
      fill: rgba(0, 0, 0, 0.3)

  h2
    margin: $gap-large 0 0

  p
    text-align: center
    margin: $gap-medium 0

  .line-item-image
    display: none

    @include media($media-gt-medium)
      display: table-cell

  .order-details
    margin-top: $gap-small
    width: calc(100% - 15px)
    margin: $gap-medium auto

    .line-items-total
      background: rgba(0, 0, 0, 0.04)

    .product-qty
      .option-name, .editable-qty
        display: none

      @include media($media-gt-medium)
        .editable-qty
          display: flex

        span
          display: none

    .editable-qty
      .btn
        padding: 0

    td
      font-size: 12px
      border: none
      border-bottom: 1px solid #d2d2d2
      padding: $gap-small

    img
      max-width: 100px
      border-radius: 5px

  .error
    color: $color-error!important

  form
    max-height: 80vh
    padding: $gap-medium $gap-small
    overflow: auto

  label
    display: block
    text-transform: capitalize
    margin: 20px auto 10px

  input, textarea
    padding: 10px
    border-radius: 3px
    width: 100%
    border: 1px solid rgba(0, 0, 0, 0.25)!important

    &:focus
      outline: none

  .btn
    display: block
    box-shadow: 0 0 10px #00000030

    &.send-message
      background: #c7ac7f
      color: #fff
      margin: $gap-medium auto
      font-weight: bold
      animation: pulse 1s infinite ease-in-out

      @include media($media-gt-medium)
        display: block
        width: auto

    @keyframes pulse
      0%
        transform: scale(0.98)
      50%
        transform: scale(1)
      100%
        transform: scale(0.98)
</style>
